/**
 * Kinetik reaktor tugunlari (5-bosqich): reaksiyalar kutubxonadan o'qiladi.
 *
 * CSTR (turg'un):   F_i,out = F_i,in + V Σ_r ν_ir r_r(C_out, T_out)
 *                   noma'lumlar: ξ_r (R ta) [+ T_out adiabatik]; Newton (sonli Yakobian, damping)
 *                   energiya: H(F_out, T_out) − H_in − Σ ξ_r(−ΔH_r) = 0
 *                   Ko'p turg'un holat bo'lishi mumkin (adiabatik ekzotermik) — `guess` boshlang'ich
 *                   konversiya qaysi shoxga tushishni belgilaydi; bu xato emas, fizika.
 * PFR:              dF_i/dV = Σ_r ν_ir r_r,   dT/dV = Σ_r (−ΔH_r) r_r / Σ F_i cp_i (adiabatik), RK4
 * Qavatli reaktor:  adiabatik PFR qavatlar ketma-ket, orasida sovitgich (T_intercool);
 *                   har qavat uchun T_in, T_out, yig'ma konversiya va muvozanat konversiyasi.
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Bosim o'zgarmas (gidravlik qarshilik yo'q); ideal gaz / ideal eritma.
 *   2. ΔH_r(T) Kirchhoff bo'yicha: ΔH_r,ref (298 K) + Σ ν_i h_i(T), pack Cp lari bilan —
 *      shuning uchun reaktor energiya balansi sxemaning umumiy balansi bilan izchil.
 *   3. PFR: ideal siqib chiqarish, bo'ylama dispersiya yo'q; CSTR: ideal aralashuv.
 *   4. Katalizator faolligi vaqt bo'yicha emas, `catalystAge` parametri bilan (turg'un rejim).
 *   5. Konversiya 0..1 oralig'ida; manfiy sarf 0 ga tortiladi (ogohlantirish bilan).
 *
 * MANBA: Fogler, 2, 8, 11–12-boblar; Levenspiel, 5–9-boblar.
 */
import { catalystActivity, concentrations, equilibriumConversion, flowHeatCapacity, getReaction, parseReaction, reactionRate, speciesEnthalpy, type ReactionDef } from '../reactions';
import { newtonSystem, rk4Step } from '../solvers';
import { warn, type Phase, type WarningSink } from '../types';
import type { FlowsheetNode } from './schema';
import { cloneStream, enthalpy, temperatureFromEnthalpy, type Stream } from './stream';

export interface ReactorCtx {
  components: readonly string[];
  phase: Phase;
  Tref: number;
  warnings: WarningSink;
}

export interface BedReport {
  Tin: number;
  Tout: number;
  /** yig'ma kalit konversiya qavat chiqishida */
  conversion: number;
  /** muvozanat konversiyasi (qavat chiqish T va kirish tarkibi bo'yicha; bitta reaksiya) */
  equilibriumConversion: number | null;
  coolerQ: number;
}

export interface KineticResult {
  outlet: Stream;
  Q: number;
  extent: Record<string, number>;
  reactionHeat: number;
  /** kalit reagent konversiyasi har reaksiya kaliti bo'yicha */
  conversion: Record<string, number>;
  /** reaksiya → kalit sarfidagi ulushi (bir xil kalitli reaksiyalar uchun) */
  selectivity: Record<string, number>;
  equilibriumConversion?: Record<string, number>;
  beds?: BedReport[];
  profile?: { V: number; T: number; X: number }[];
  /** adiabatik/sovitiladigan bitta reaksiyali CSTR: barcha turg'un holatlar (ko'plik) */
  steadyStates?: { T: number; conversion: number }[];
  converged: boolean;
}

export function resolveReactions(list: readonly (string | object)[]): ReactionDef[] {
  return list.map((r) => (typeof r === 'string' ? getReaction(r) : parseReaction(r)));
}

type KineticNode = Extract<FlowsheetNode, { type: 'cstr' | 'pfr' | 'bed-reactor' }>;

function num(v: number | string | undefined, what: string, dflt?: number): number {
  if (v === undefined) {
    if (dflt !== undefined) return dflt;
    throw new Error(`${what}: required`);
  }
  if (typeof v !== 'number') throw new Error(`${what}: unresolved variant reference ${v}`);
  return v;
}

function applyExtents(flowIn: Record<string, number>, rxns: ReactionDef[], xi: number[], warnings: WarningSink, node: string): Record<string, number> {
  const F = { ...flowIn };
  rxns.forEach((r, k) => {
    for (const [c, nu] of Object.entries(r.stoich)) F[c] = (F[c] ?? 0) + nu * (xi[k] as number);
  });
  for (const c of Object.keys(F)) {
    if ((F[c] as number) < -1e-9) warn(warnings, 'flowsheet.negativeFlow', 'warning', { node, component: c });
    F[c] = Math.max(0, F[c] as number);
  }
  return F;
}

function keyStats(rxns: ReactionDef[], xi: number[], flowIn: Record<string, number>) {
  const conversion: Record<string, number> = {};
  const consumedByKey: Record<string, number> = {};
  rxns.forEach((r, k) => {
    const used = (xi[k] as number) * -(r.stoich[r.key] as number);
    consumedByKey[r.key] = (consumedByKey[r.key] ?? 0) + used;
  });
  for (const [key, used] of Object.entries(consumedByKey)) conversion[key] = (flowIn[key] ?? 0) > 0 ? used / (flowIn[key] as number) : 0;
  const selectivity: Record<string, number> = {};
  rxns.forEach((r, k) => {
    const used = (xi[k] as number) * -(r.stoich[r.key] as number);
    const tot = consumedByKey[r.key] ?? 0;
    selectivity[r.id] = tot > 0 ? used / tot : 0;
  });
  return { conversion, selectivity };
}

/**
 * Ideal aralashuvli reaktor — turg'un rejim.
 * Bitta reaksiya, adiabatik/sovitiladigan: 1-o'lchovli usul — har T uchun moddiy balansdan ξ(T)
 * (monoton, bisection), so'ng energiya qoldig'i E(T) = H_out − H_in − ξ(−ΔH) − Q(T) ning
 * ildizlari T panjarasida qidiriladi (ko'p turg'un holat!), `guessT` ga eng yaqini tanlanadi;
 * barcha ildizlar `steadyStates` da qaytariladi. Boshqa holatlarda — Newton (ξ_r, T).
 * Sovitish: Q = duty + UA·(Tj − T).
 */
export function computeCstr(node: Extract<KineticNode, { type: 'cstr' }>, inn: Stream, outletId: string, ctx: ReactorCtx): KineticResult {
  const rxns = resolveReactions(node.reactions);
  const V = num(node.volume, `${node.id}.volume`);
  const age = num(node.catalystAge, `${node.id}.catalystAge`, 0);
  const acts = rxns.map((r) => catalystActivity(r, age));
  const adiabatic = node.mode !== 'isothermal';
  const duty0 = node.mode === 'cooled' ? num(node.duty, `${node.id}.duty`, 0) : 0;
  const UA = node.mode === 'cooled' ? num(node.UA, `${node.id}.UA`, 0) : 0;
  const Tj = node.mode === 'cooled' ? num(node.Tj, `${node.id}.Tj`, inn.T) : inn.T;
  const Qof = (T: number) => duty0 + UA * (Tj - T);
  const Tiso = node.mode === 'isothermal' ? num(node.T, `${node.id}.T`, inn.T) : inn.T;
  const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
  const xiMax = rxns.map((r) => (inn.flow[r.key] ?? 0) / -(r.stoich[r.key] as number));
  const heatOf = (xi: number[]) => rxns.reduce((h, r, k) => h + (xi[k] as number) * -r.dHr, 0);
  const guessT = num(node.guessT, `${node.id}.guessT`, inn.T);
  let steadyStates: { T: number; conversion: number }[] | undefined;
  let xi: number[];
  let T: number;
  let converged = true;

  // moddiy balans bitta reaksiya uchun: ξ − V r(ξ, T) = 0, ξ ∈ [0, ξmax] (bisection)
  const xiAtT = (Tv: number): number => {
    const r0 = rxns[0] as ReactionDef;
    const xm = xiMax[0] as number;
    if (xm <= 0) return 0;
    const g = (x: number) => x - V * reactionRate(r0, concentrations(applyExtents(inn.flow, rxns, [x], [], node.id), Tv, inn.P, ctx.phase), Tv, acts[0]);
    let lo = 0;
    let hi = xm * (1 - 1e-12);
    if (g(lo) >= 0) return 0;
    if (g(hi) <= 0) return hi;
    for (let i = 0; i < 80; i++) {
      const mid = 0.5 * (lo + hi);
      if (g(mid) < 0) lo = mid;
      else hi = mid;
    }
    return 0.5 * (lo + hi);
  };

  if (rxns.length === 1 && adiabatic) {
    const cp = Math.max(1e-6, flowHeatCapacity(inn.flow, inn.T, ctx.phase));
    const dTad = Math.abs(heatOf([xiMax[0] as number])) / cp;
    const Tlo = Math.max(150, Math.min(inn.T, Tj) - 50 - (UA > 0 ? 0 : dTad * 0.1));
    // mahsulot cp kichikroq bo'lishi mumkin — yuqori chegara zaxira bilan
    const Thi = Math.max(inn.T, Tj) + 2 * dTad + 100;
    const Ef = (Tv: number) => {
      const x = xiAtT(Tv);
      const out: Stream = { id: outletId, flow: applyExtents(inn.flow, rxns, [x], [], node.id), T: Tv, P: inn.P };
      return enthalpy(out, ctx.phase, ctx.Tref) - Hin - heatOf([x]) - Qof(Tv);
    };
    const N = 240;
    const roots: number[] = [];
    let prevT = Tlo;
    let prevE = Ef(Tlo);
    for (let i = 1; i <= N; i++) {
      const Tv = Tlo + ((Thi - Tlo) * i) / N;
      const Ev = Ef(Tv);
      if (Number.isFinite(prevE) && Number.isFinite(Ev) && prevE * Ev <= 0 && prevE !== Ev) {
        let lo = prevT;
        let hi = Tv;
        let elo = prevE;
        for (let k = 0; k < 60; k++) {
          const mid = 0.5 * (lo + hi);
          const em = Ef(mid);
          if (elo * em <= 0) hi = mid;
          else {
            lo = mid;
            elo = em;
          }
        }
        roots.push(0.5 * (lo + hi));
      }
      prevT = Tv;
      prevE = Ev;
    }
    if (roots.length === 0) {
      converged = false;
      T = inn.T;
      xi = [xiAtT(T)];
      warn(ctx.warnings, 'reactor.notConverged', 'critical', { node: node.id, residual: Math.abs(Ef(T)) });
    } else {
      T = roots.reduce((best, r) => (Math.abs(r - guessT) < Math.abs(best - guessT) ? r : best), roots[0] as number);
      xi = [xiAtT(T)];
    }
    const nuKey = -((rxns[0] as ReactionDef).stoich[(rxns[0] as ReactionDef).key] as number);
    const F0 = inn.flow[(rxns[0] as ReactionDef).key] ?? 0;
    steadyStates = roots.map((r) => ({ T: r, conversion: F0 > 0 ? (xiAtT(r) * nuKey) / F0 : 0 }));
  } else {
    // umumiy holat: Newton (o'lchovsiz u_r = ξ_r/ξ_max, τ = (T − T_in)/100)
    const cpScale = Math.max(1e-6, flowHeatCapacity(inn.flow, inn.T, ctx.phase));
    const evalAt = (u: readonly number[]) => {
      const x = rxns.map((_, k) => Math.min(1, Math.max(0, u[k] as number)) * (xiMax[k] as number));
      const Tv = adiabatic ? inn.T + 100 * (u[rxns.length] as number) : Tiso;
      const F = applyExtents(inn.flow, rxns, x, [], node.id);
      const C = concentrations(F, Tv, inn.P, ctx.phase);
      const res = rxns.map((r, k) => {
        const scale = Math.max((xiMax[k] as number) * 1e-3, 1e-12);
        const penalty = ((u[k] as number) - Math.min(1, Math.max(0, u[k] as number))) * 10;
        return ((x[k] as number) - V * reactionRate(r, C, Tv, acts[k])) / Math.max(xiMax[k] as number, scale) + penalty;
      });
      if (adiabatic) {
        const out: Stream = { id: outletId, flow: F, T: Tv, P: inn.P };
        res.push((enthalpy(out, ctx.phase, ctx.Tref) - Hin - heatOf(x) - Qof(Tv)) / (cpScale * 100));
      }
      return { res, x, Tv };
    };
    const u0 = rxns.map((r) => num(node.guess?.[r.id], `${node.id}.guess`, 0.3));
    if (adiabatic) u0.push((guessT - inn.T) / 100);
    const sol = newtonSystem((u) => evalAt(u).res, u0, { ftol: 1e-10, xtol: 1e-12, maxIter: 200, damping: true });
    converged = sol.converged;
    if (!sol.converged) warn(ctx.warnings, 'reactor.notConverged', 'critical', { node: node.id, residual: sol.residual });
    const r = evalAt(sol.value);
    xi = r.x;
    T = r.Tv;
  }
  const F = applyExtents(inn.flow, rxns, xi, ctx.warnings, node.id);
  const out: Stream = { id: outletId, flow: F, T, P: inn.P };
  const heat = heatOf(xi);
  const Q = node.mode === 'cooled' ? Qof(T) : adiabatic ? 0 : enthalpy(out, ctx.phase, ctx.Tref, ctx.warnings) - Hin - heat;
  if (node.maxT !== undefined && T > num(node.maxT, `${node.id}.maxT`)) warn(ctx.warnings, 'reactor.runaway', 'critical', { node: node.id, T: Math.round(T) });
  const extent = Object.fromEntries(rxns.map((r, k) => [r.id, xi[k] as number]));
  const eq = Object.fromEntries(rxns.map((r) => [r.id, equilibriumConversion(r, inn.flow, T, inn.P)]));
  return { outlet: out, Q, extent, reactionHeat: heat, ...keyStats(rxns, xi, inn.flow), equilibriumConversion: eq, ...(steadyStates ? { steadyStates } : {}), converged };
}

/**
 * Siqib chiqarish reaktori — RK4 bo'ylama integratsiya.
 * Holat: y = [F_1..F_N, T, ξ_1..ξ_R] — ξ ham integrallanadi (dξ_r/dV = r_r), shunda F va ξ izchil.
 * Qattiqlik (stiffness): qaytar reaksiya muvozanatga yaqin katta k bilan RK4 ni tebratadi —
 * har dV ichida adaptiv bo'laklar: nisbiy o'zgarish max |dF_i|·h / F_i ≤ 2 % bo'lguncha h kamaytiriladi.
 */
function integratePfr(inn: Stream, rxns: ReactionDef[], acts: number[], V: number, steps: number, adiabatic: boolean, ctx: ReactorCtx, profile?: { V: number; T: number; X: number }[]) {
  const comps = ctx.components;
  const N = comps.length;
  const R = rxns.length;
  const keyId = rxns[0]?.key ?? '';
  const F0key = inn.flow[keyId] ?? 0;
  const Ftot = comps.reduce((a, c) => a + (inn.flow[c] ?? 0), 0);
  const eps = 1e-9 * Math.max(Ftot, 1e-9);
  const y0 = [...comps.map((c) => inn.flow[c] ?? 0), inn.T, ...rxns.map(() => 0)];
  const deriv = (_v: number, y: readonly number[]): number[] => {
    const F: Record<string, number> = {};
    comps.forEach((c, i) => (F[c] = Math.max(0, y[i] as number)));
    const T = y[N] as number;
    const C = concentrations(F, T, inn.P, ctx.phase);
    const rates = rxns.map((r, k) => reactionRate(r, C, T, acts[k]));
    const dF = comps.map((c) => rxns.reduce((a, r, k) => a + (r.stoich[c] ?? 0) * (rates[k] as number), 0));
    let dT = 0;
    if (adiabatic) {
      // Kirchhoff: ΔH_r(T) = ΔH_r,ref + Σ ν_i h_i(T), h_i — sezgir entalpiya (Tref bazasi)
      const h = speciesEnthalpy(comps, T, ctx.phase, ctx.Tref);
      const heat = rxns.reduce((a, r, k) => {
        const dHT = r.dHr + Object.entries(r.stoich).reduce((x, [c, nu]) => x + nu * (h[c] ?? 0), 0);
        return a + -dHT * (rates[k] as number);
      }, 0);
      dT = heat / Math.max(1e-9, flowHeatCapacity(F, T, ctx.phase));
    }
    return [...dF, dT, ...rates];
  };
  let y = y0;
  const dV = V / steps;
  for (let s = 0; s < steps; s++) {
    let v = s * dV;
    let left = dV;
    let guard = 0;
    while (left > 1e-15 * dV && guard++ < 10000) {
      const d = deriv(v, y);
      // adaptiv qadam: komponent sarfi 2 % dan ko'p o'zgarmasin, T 2 K dan ko'p emas
      let h = left;
      for (let i = 0; i < N; i++) {
        const Fi = Math.max(y[i] as number, eps);
        const di = Math.abs(d[i] as number);
        if (di > 0) h = Math.min(h, (0.02 * Fi) / di);
      }
      const dT = Math.abs(d[N] as number);
      if (dT > 0) h = Math.min(h, 2 / dT);
      h = Math.max(h, left / 10000);
      h = Math.min(h, left);
      y = rk4Step(deriv, v, y, h);
      for (let i = 0; i < N; i++) if ((y[i] as number) < 0) y[i] = 0;
      v += h;
      left -= h;
    }
    if (profile) {
      const Fk = Math.max(0, y[comps.indexOf(keyId)] as number);
      profile.push({ V: (s + 1) * dV, T: y[N] as number, X: F0key > 0 ? 1 - Fk / F0key : 0 });
    }
  }
  const F: Record<string, number> = {};
  comps.forEach((c, i) => (F[c] = Math.max(0, y[i] as number)));
  const xi = Array.from({ length: R }, (_, k) => y[N + 1 + k] as number);
  return { F, T: y[N] as number, xi };
}

export function computePfr(node: Extract<KineticNode, { type: 'pfr' }>, inn: Stream, outletId: string, ctx: ReactorCtx): KineticResult {
  const rxns = resolveReactions(node.reactions);
  const V = num(node.volume, `${node.id}.volume`);
  const age = num(node.catalystAge, `${node.id}.catalystAge`, 0);
  const acts = rxns.map((r) => catalystActivity(r, age));
  const adiabatic = node.mode === 'adiabatic';
  const start = cloneStream(inn);
  if (!adiabatic && node.T !== undefined) start.T = num(node.T, `${node.id}.T`);
  const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
  const profile = node.profile ? [] : undefined;
  const { F, T, xi } = integratePfr(start, rxns, acts, V, node.steps, adiabatic, ctx, profile);
  const out: Stream = { id: outletId, flow: F, T, P: inn.P };
  const heat = rxns.reduce((a, r, k) => a + (xi[k] as number) * -r.dHr, 0);
  const Q = adiabatic ? 0 : enthalpy(out, ctx.phase, ctx.Tref, ctx.warnings) - Hin - heat;
  const extent = Object.fromEntries(rxns.map((r, k) => [r.id, xi[k] as number]));
  const eq = Object.fromEntries(rxns.map((r) => [r.id, equilibriumConversion(r, inn.flow, T, inn.P)]));
  return { outlet: out, Q, extent, reactionHeat: heat, ...keyStats(rxns, xi, inn.flow), equilibriumConversion: eq, ...(profile ? { profile } : {}), converged: true };
}

/** Qavatli adiabatik katalitik reaktor, oraliq sovitish bilan */
export function computeBedReactor(node: Extract<KineticNode, { type: 'bed-reactor' }>, inn: Stream, outletId: string, ctx: ReactorCtx): KineticResult {
  const rxns = resolveReactions(node.reactions);
  const Hin = enthalpy(inn, ctx.phase, ctx.Tref, ctx.warnings);
  let cur = cloneStream(inn);
  if (node.inletT !== undefined) cur.T = num(node.inletT, `${node.id}.inletT`);
  let Qcool = enthalpy(cur, ctx.phase, ctx.Tref) - Hin; // kirish isitgichi/sovitgichi (bo'lsa)
  const xiTotal = rxns.map(() => 0);
  const beds: BedReport[] = [];
  const keyId = rxns[0]?.key ?? '';
  const F0key = inn.flow[keyId] ?? 0;
  node.beds.forEach((bed, i) => {
    const age = num(bed.catalystAge, `${node.id}.beds[${i}].catalystAge`, 0);
    const acts = rxns.map((r) => catalystActivity(r, age));
    const V = num(bed.volume, `${node.id}.beds[${i}].volume`);
    const Tin = cur.T;
    const { F, T, xi } = integratePfr(cur, rxns, acts, V, node.steps, true, ctx);
    rxns.forEach((_, k) => (xiTotal[k] = (xiTotal[k] as number) + (xi[k] as number)));
    const eq = rxns.length === 1 ? equilibriumConversion(rxns[0] as ReactionDef, inn.flow, T, inn.P) : null;
    let coolerQ = 0;
    cur = { id: cur.id, flow: F, T, P: cur.P };
    if (i < node.beds.length - 1) {
      const Tcool = Array.isArray(node.intercoolT) ? num(node.intercoolT[i], `${node.id}.intercoolT[${i}]`) : num(node.intercoolT, `${node.id}.intercoolT`);
      const before = enthalpy(cur, ctx.phase, ctx.Tref);
      cur.T = Tcool;
      coolerQ = enthalpy(cur, ctx.phase, ctx.Tref) - before;
      Qcool += coolerQ;
    }
    beds.push({ Tin, Tout: T, conversion: F0key > 0 ? 1 - (F[keyId] ?? 0) / F0key : 0, equilibriumConversion: eq, coolerQ });
  });
  const out: Stream = { id: outletId, flow: cur.flow, T: cur.T, P: cur.P };
  const heat = rxns.reduce((a, r, k) => a + (xiTotal[k] as number) * -r.dHr, 0);
  const extent = Object.fromEntries(rxns.map((r, k) => [r.id, xiTotal[k] as number]));
  return { outlet: out, Q: Qcool, extent, reactionHeat: heat, ...keyStats(rxns, xiTotal, inn.flow), beds, converged: true };
}

/** Umumiy kirish nuqtasi: tugun turiga qarab */
export function computeKineticReactor(node: KineticNode, inn: Stream, outletId: string, ctx: ReactorCtx): KineticResult {
  switch (node.type) {
    case 'cstr':
      return computeCstr(node, inn, outletId, ctx);
    case 'pfr':
      return computePfr(node, inn, outletId, ctx);
    case 'bed-reactor':
      return computeBedReactor(node, inn, outletId, ctx);
  }
}

/** Adiabatik harorat ko'tarilishi tekshiruvi uchun yordamchi: ΔT_ad ≈ ξ(−ΔH)/Σ F cp */
export function adiabaticRise(rxn: ReactionDef, xi: number, flow: Record<string, number>, T: number, phase: Phase): number {
  return (xi * -rxn.dHr) / flowHeatCapacity(flow, T, phase);
}

export { temperatureFromEnthalpy };
